import { inject, Injectable } from '@angular/core';
import {
    InventoryBundleStore,
    PickStore,
    ProjectDto,
    WarehouseDto,
} from '@pf/platform-wms/domain';
import { endOfDay, endOfToday, startOfDay } from 'date-fns';
import { sumBy, uniq, uniqBy } from 'lodash-es';
import { CpwProjectTypePriority } from '../models/cpw-project-types';
import { ProjectSummary } from '../models/project-summary.model';
import { PickingTask } from '../models/picking-task.model';
import { CpwTaskStateService } from '../infrastructure/cpw-task-state.service';
import { asArraySafe, HashObject } from '@pf/shared-utility';
import { ProjectSummaryFactory } from '../factories/project-summary.factory';
import {
    EquipmentGroup,
    EquipmentGroupIcons,
} from '../models/cpw-equipment-types';
import { TargetContainers } from '../models/picking-context.model';

const DEFAULT_BATCH_SIZE = 20;

@Injectable()
export class PickingTaskOrchestrator {
    private readonly taskStateService = inject(CpwTaskStateService);
    private readonly projectSummaryFactory = inject(ProjectSummaryFactory);
    private readonly pickStore = inject(PickStore);
    private readonly inventoryBundleStore = inject(InventoryBundleStore);

    orchestrate(
        warehouse: WarehouseDto,
        pendingProjects: ProjectDto[],
        pickingProjects: ProjectDto[],
        showLtlProjects: boolean
    ) {
        const warehouseId = warehouse.id;
        const batchSize =
            warehouse.settings?.pickingBatchSize ?? DEFAULT_BATCH_SIZE;

        const pendingProjectSummaries = pendingProjects
            .sort((a, b) => {
                const aEndDate = new Date(a.endDate as string);
                const bEndDate = new Date(b.endDate as string);
                if (aEndDate < bEndDate) return -1;
                if (aEndDate > bEndDate) return 1;
                return new Date(a.createdDate as string) <
                    new Date(b.createdDate as string)
                    ? -1
                    : 1;
            })
            .map(project => this.projectSummaryFactory.create(project))
            .filter(
                summary =>
                    summary.totalPicks > 0 &&
                    (showLtlProjects || !summary.isLtl)
            );

        const pickingProjectSummaries = pickingProjects.map(project =>
            this.projectSummaryFactory.create(project)
        );

        const pendingTasks = this.getPickingGroups(
            warehouseId,
            batchSize,
            pendingProjectSummaries
        );
        const pickingTasks = this.getPickingTasks(
            warehouseId,
            batchSize,
            'picking',
            false,
            [
                [
                    this.groupByEquipmentType(pickingProjectSummaries)
                        .keys()
                        .next().value || 'Parts',
                    pickingProjectSummaries,
                ],
            ]
        );

        if (pickingProjects.length > 0) {
            pendingTasks.forEach(task => {
                task.readonly = true;
            });

            pickingTasks.forEach(task => {
                task.claimedAt = pickingProjects[0].modifiedDate;
            });
        }

        this.taskStateService.setTasks([...pendingTasks, ...pickingTasks]);
    }

    private getGroup(projectSummary: ProjectSummary): EquipmentGroup {
        if (projectSummary.isUniforms) {
            return 'Uniforms';
        }
        return projectSummary.isLtl ? 'LTL' : 'Parts';
    }

    private groupByEquipmentType(projects: ProjectSummary[]) {
        return projects.reduce((pv, summary) => {
            const groupName = this.getGroup(summary);
            if (!pv.has(groupName)) pv.set(groupName, []);
            pv.get(groupName)?.push(summary);
            return pv;
        }, new Map<EquipmentGroup, ProjectSummary[]>());
    }

    private getPickingGroups(
        warehouseId: string,
        batchSize: number,
        projectSummaries: ProjectSummary[]
    ) {
        const priorityGroup = projectSummaries.filter(
            summary => !!CpwProjectTypePriority[summary.projectTypeId]
        );
        const nonPriorityGroup = projectSummaries.filter(
            summary => !CpwProjectTypePriority[summary.projectTypeId]
        );

        const today = endOfToday();

        const dueToday = nonPriorityGroup.filter(
            summary => startOfDay(summary.endDate) <= today
        );
        const dueAfterToday = nonPriorityGroup.filter(
            summary => endOfDay(summary.endDate) > today
        );

        const priorityGrouped = priorityGroup.map(summary =>
            this.getPickingTask(
                warehouseId,
                'priority',
                true,
                [summary],
                this.getGroup(summary)
            )
        );

        const dueTodayLtl = dueToday.filter(summary => summary.isLtl);
        const dueTodayNonLtl = dueToday.filter(summary => !summary.isLtl);
        const dueAfterTodayLtl = dueAfterToday.filter(summary => summary.isLtl);
        const dueAfterTodayNonLtl = dueAfterToday.filter(
            summary => !summary.isLtl
        );

        const nonPriorityDueTodayGrouped =
            this.groupByEquipmentType(dueTodayNonLtl);
        const nonPriorityDueAfterTodayGrouped =
            this.groupByEquipmentType(dueAfterTodayNonLtl);

        const ltlTodayTasks = dueTodayLtl.map(summary =>
            this.getPickingTask(warehouseId, 'today', false, [summary], 'LTL')
        );
        const ltlFutureTasks = dueAfterTodayLtl.map(summary =>
            this.getPickingTask(warehouseId, 'future', false, [summary], 'LTL')
        );

        const todayTasks = [
            ...ltlTodayTasks,
            ...this.getPickingTasks(
                warehouseId,
                batchSize,
                'today',
                false,
                Array.from(nonPriorityDueTodayGrouped.entries())
            ),
        ];

        const futureTasks = [
            ...ltlFutureTasks,
            ...this.getPickingTasks(
                warehouseId,
                batchSize,
                'future',
                false,
                Array.from(nonPriorityDueAfterTodayGrouped.entries())
            ),
        ];

        if (todayTasks.length > 0) {
            futureTasks.forEach(task => {
                task.readonly = true;
            });
        }

        return [...priorityGrouped, ...todayTasks, ...futureTasks];
    }

    private getPickingTasks(
        warehouseId: string,
        batchSize: number,
        section: PickingTask['section'],
        priority: boolean,
        groups: [EquipmentGroup, ProjectSummary[]][]
    ) {
        const tasks: PickingTask[] = [];
        groups.forEach(([group, projects]) => {
            projects
                .reduce((buckets, project, index) => {
                    const groupIndex = Math.floor(index / batchSize);
                    if (!buckets[groupIndex]) buckets[groupIndex] = [];
                    buckets[groupIndex].push(project);
                    return buckets;
                }, [] as ProjectSummary[][])
                .forEach(bucket => {
                    const task = this.getPickingTask(
                        warehouseId,
                        section,
                        priority,
                        bucket,
                        group
                    );
                    tasks.push(task);
                });
        });

        return tasks;
    }

    private getPickingTask(
        warehouseId: string,
        section: PickingTask['section'],
        priority: boolean,
        projectSummaries: ProjectSummary[],
        group: EquipmentGroup
    ) {
        const title =
            projectSummaries.length > 1
                ? `${projectSummaries.length} Orders`
                : projectSummaries[0].projectName;
        const totalItems = sumBy(projectSummaries, x => x.totalQuantityToPick);
        const zones = uniqBy(
            projectSummaries.map(summary => summary.zones).flat(),
            x => x.id
        );
        const id = HashObject(projectSummaries.map(x => x.projectId).sort());
        const assignedHandlingUnitIds =
            this.getAssignedHandlingUnitIds(projectSummaries);
        const targetContainersByProject =
            this.getTargetContainersByProject(projectSummaries);
        return {
            id,
            warehouseId,
            section,
            title,
            details: `${totalItems} items - Zones: ${zones.length}`,
            time: projectSummaries[0].createdDate,
            priority,
            taskIconType: EquipmentGroupIcons[group],
            group,
            projectSummaries: projectSummaries,
            readonly: false,
            context: {
                projectIds: projectSummaries.map(x => x.projectId),
                assignedHandlingUnitIds,
                targetContainersByProject,
            },
            isLtl: group === 'LTL',
            isUniforms: group === 'Uniforms',
        } as PickingTask;
    }

    private getAssignedHandlingUnitIds(
        projectSummaries: ProjectSummary[]
    ): string[] {
        const equipmentIds: string[] = [];

        projectSummaries.forEach(summary => {
            summary.pickListIds.forEach(pickListId => {
                const pick = this.pickStore.get(pickListId);
                if (!pick) return;

                asArraySafe(pick.pickLocations).forEach(pickLocation => {
                    if (!pickLocation.inventoryBundleId) return;

                    const bundle = this.inventoryBundleStore.get(
                        pickLocation.inventoryBundleId
                    );
                    if (!bundle?.container?.equipment?.id) return;

                    // Only include containers assigned to equipment (not slots)
                    if (!bundle.container.slotId) {
                        equipmentIds.push(bundle.container.equipment.id);
                    }
                });
            });
        });

        return uniq(equipmentIds);
    }

    private getTargetContainersByProject(
        projectSummaries: ProjectSummary[]
    ): Record<string, TargetContainers> {
        const targetContainersByProject: Record<string, TargetContainers> = {};

        projectSummaries.forEach(summary => {
            const projectContainers = new Map<
                string,
                { containerId: string; equipmentTypeId?: string }
            >();

            summary.pickListIds.forEach(pickListId => {
                const pick = this.pickStore.get(pickListId);
                if (!pick) return;

                asArraySafe(pick.pickLocations).forEach(pickLocation => {
                    // Check if there's a pickToContainerId (container being picked into)
                    if (pickLocation.pickToContainerId) {
                        const bundle = pickLocation.inventoryBundleId
                            ? this.inventoryBundleStore.get(
                                  pickLocation.inventoryBundleId
                              )
                            : null;

                        projectContainers.set(pickLocation.pickToContainerId, {
                            containerId: pickLocation.pickToContainerId,
                            equipmentTypeId:
                                bundle?.container?.equipment?.equipmentType?.id,
                        });
                    }
                });
            });

            if (projectContainers.size > 0) {
                const containers = Array.from(projectContainers.values());
                targetContainersByProject[summary.projectId] = {
                    latestContainer: containers[containers.length - 1],
                    containers: containers,
                };
            }
        });

        return targetContainersByProject;
    }
}
